/**
 * The five headline numbers. Four are the levelled walk's own conclusions; the fifth is
 * trailing utilisation, passed in as a child because it comes from a different question
 * (what the machines DID) and a different feed.
 *
 * "Groups that never clear" NAMES them. A count on its own is a fact nobody can act on,
 * and the names are what turn "2 of 6" into a conversation about EDM5 and MATSURA.
 */

import React from 'react'
import type { LoadingKpis } from '../../viewmodels/loading'
import { hrs } from './format'

const CARD = 'flex flex-col gap-[3px] rounded-[9px] border px-3 py-2.5'
const LABEL = 'font-mono text-[9.5px] font-semibold uppercase tracking-[.09em]'

function KpiCard({ label, value, note, tone }: {
  label: string
  value: string
  note: string
  tone?: 'good' | 'bad'
}): React.JSX.Element {
  const color = tone === 'bad' ? 'var(--lc-red)' : tone === 'good' ? 'var(--lc-teal)' : 'var(--lc-ink)'
  return (
    <div className={CARD} style={{ borderColor: 'var(--lc-b09)', background: 'var(--lc-panel)' }}>
      <div className={LABEL} style={{ color: 'var(--lc-mut)' }}>{label}</div>
      <div className="font-sans text-[19px] font-semibold" style={{ color }}>{value}</div>
      <div className="font-sans text-[10.5px] leading-[1.35]" style={{ color: 'var(--lc-mut)' }}>{note}</div>
    </div>
  )
}

/** `weeks` is the SERVED window, not a literal: the horizon is a choice since 31 Aug 2026,
 *  and both of these labels are claims about how far forward the number looks. */
export default function KpiRow({ kpis, weeks, children }: {
  kpis: LoadingKpis
  weeks: number
  children: React.ReactNode
}): React.JSX.Element {
  const names = kpis.never.map((r) => r.name).join(', ')
  return (
    <div className="grid grid-cols-2 items-stretch gap-2.5 px-[22px] pt-3.5 md:grid-cols-3 xl:grid-cols-5">
      <KpiCard label="Overdue in" value={hrs(kpis.overdue)} note="due before this week" tone="bad" />
      <KpiCard label={`Free capacity · ${weeks}wk`} value={hrs(kpis.free)} note="after backlog and plan" tone="good" />
      <KpiCard
        label={`Still queued at wk ${weeks}`}
        value={hrs(kpis.queued)}
        note={kpis.queued > 0 ? 'does not clear in horizon' : 'clears inside horizon'}
        tone={kpis.queued > 0 ? 'bad' : 'good'}
      />
      <KpiCard
        label="Groups that never clear"
        value={`${kpis.never.length} of ${kpis.total}`}
        note={names || 'all clear in horizon'}
        tone={kpis.never.length ? 'bad' : 'good'}
      />
      {children}
    </div>
  )
}
